import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import _ from 'lodash';

import { useContainerSelector, useWidgetSelectorForType } from '../../components/widgets/core/hooks';
import { WidgetsContext, Widget } from '../../components/widgets/core';
import Portal from '../../components/Portal';

import { HOTSPOT } from '../../constants/widgetTypes';

export const HotspotContext = React.createContext({
  active: null,
  onMouseOver: () => {},
  onMouseOut: () => {},
  onClick: () => {}
})

export const HotspotRender = ({ breakpoint, children}) => {

  const [state, setState] = useState({
    active: null,
    parent: null
  })

  const stateRef = useRef(state);

  const context = React.useContext(WidgetsContext);

  const hotspotWidgets = useWidgetSelectorForType(HOTSPOT);

  useEffect(() => {
    if (!context.development) {
      window.addEventListener("click", hideHotspotForClick);

      return () => {
        window.removeEventListener("click", hideHotspotForClick);
      }
    }
  }, []);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const handleMouseOver = (e, target, hotspot) => {
    var hotspotId = _.get(hotspot, 'uid', null);

    if(!hotspotId) {
      return;
    }

    var hotspotElement = document.getElementById(hotspotId);
    var targetElement = document.getElementById(target.uid);

    hotspotElement.setAttribute("data-hover-on", true);
    targetElement.setAttribute("data-hover-on", true);

    setState((state) => {
      return {
        ...state,
        active: hotspotId,
        parent: target.uid
      }
    })
  }

  const handleMouseOut = (e, target, hotspot) => {
    var hotspotId = _.get(hotspot, 'uid', null);
    var targetId = _.get(target, 'uid', null);

    if (!hotspotId) {
      return;
    }

    var hotspotElement = document.getElementById(hotspotId);
    var targetElement = document.getElementById(targetId);

    hotspotElement.removeAttribute("data-hover-on");
    targetElement.removeAttribute("data-hover-on");

    hideHotpsotForHover();
  }

  const handleClick = (e, target, hotspot) => {
    var hotspotId = _.get(hotspot, 'uid', null);
    var targetId = _.get(target, 'uid', null);

    var hotspotElement = document.getElementById(hotspotId);
    var targetElement = document.getElementById(targetId);

    hotspotElement.setAttribute("data-hover-on", true);
    targetElement.setAttribute("data-hover-on", true);

    setState((state) => {
      return {
        ...state,
        active: hotspotId,
        parent: target.uid
      }
    })
  }

  const hideHotpsotForHover = _.debounce(() => {
    var visibleHotposts = document.querySelector('[data-hover-on="true"]');
    if(!visibleHotposts || visibleHotposts.length === 0) {
      hideHotspot();
    }
  }, 500);

  const hideHotspotForClick = (event) => {
    if (!stateRef.current.active) {
      return;
    }

    var hotspotElement = document.getElementById(stateRef.current.active);
    var targetElement = document.getElementById(stateRef.current.parent);

    if (hotspotElement && !hotspotElement.contains(event.target) && targetElement && !targetElement.contains(event.target)) {
      hotspotElement.removeAttribute("data-hover-on");
      targetElement.removeAttribute("data-hover-on");

      hideHotspot();
    }
  }

  const hideHotspot = () => {
    setState((state) => {
      return {
        ...state,
        active: null,
        parent: null
      }
    })
  }

  const hotspotContextValue = React.useMemo(() => ({
    active: state.active,
    onMouseOver: handleMouseOver,
    onMouseOut: handleMouseOut,
    onClick: handleClick
  }), [state.active]);

  return (
    <HotspotContext.Provider value={hotspotContextValue}>
      {children}

      {
        (hotspotWidgets || []).map((item, index) => {
          return <Widget key={index} widgetProps={item} breakpoint={breakpoint} />
        })
      }
    </HotspotContext.Provider>
  )
}

const HotspotTriggerComponent = ({ widget, breakpoint, targetRef, children }) => {

  const hotspotContext = React.useContext(HotspotContext);

  const widgetContext = React.useContext(WidgetsContext);
  const selector = widgetContext.selector;

  var widgets = useSelector(state => _.get(state, `${selector}.widgets`) || []);
  var hotspotWidget = widgets.find(item => item.parent === widget.uid && item.type === HOTSPOT);
  const trigger = _.get(hotspotWidget, `properties.${breakpoint}.trigger`);

  useEffect(() => {
    if (hotspotWidget && trigger === 'hover') {
      targetRef.current.addEventListener('mouseover', handleMouseOver);
      targetRef.current.addEventListener('mouseout', handleMouseOut);
    }

    if (hotspotWidget && trigger === 'click') {
      targetRef.current.addEventListener('click', handleClick);
    }

    return () => {
      if (targetRef.current) {
        targetRef.current.removeEventListener('mouseover', handleMouseOver);
        targetRef.current.removeEventListener('mouseout', handleMouseOut);
      }
    }
  }, []);

  const handleMouseOver = (e) => {
    hotspotContext.onMouseOver(e, widget, hotspotWidget);
  }

  const handleMouseOut = (e) => {
    hotspotContext.onMouseOut(e, widget, hotspotWidget);
  }

  const handleClick = (e) => {
    hotspotContext.onClick(e, widget, hotspotWidget);
  }

  return children;
}

const hotspotTriggerEquals = function (prevProps, nextProps) {
  const paths = ['widget', 'breakpoint', 'targetRef', 'children'];
  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);

  return equals;
}

export const HotspotTrigger = React.memo(HotspotTriggerComponent, hotspotTriggerEquals);
